import type { ApiProblem } from '@pos/api-client';

/** Every CRUD record carries its id and version; the version is sent back as If-Match on update and delete. */
export interface CrudRecord {
  id: string;
  version: number | string;
}

export interface ListParams {
  search?: string;
  sort?: string;
  page?: number;
  pageSize?: number;
  /** List filters by query parameter, e.g. `status: 'active,inactive'` (see the backend's CrudFilter). */
  [filter: string]: string | number | undefined;
}

export interface Page<T> {
  items: T[];
  total: number;
  /** More match than the server counts (ADR 0127): `total` is that many. */
  totalCapped?: boolean;
}

export interface CrudApi<T extends CrudRecord, TInput> {
  path: string;
  list(params: ListParams): Promise<Page<T>>;
  get(id: string): Promise<T>;
  create(input: TInput): Promise<T>;
  update(record: T, input: TInput): Promise<T>;
  remove(record: T): Promise<void>;
  /** Brings a removed record back (ADR 0117), with the version it was removed at. */
  restore(record: T): Promise<T>;
}

interface FetchResult {
  data?: unknown;
  error?: unknown;
  response: Response;
}

/** The part of an openapi-fetch client the generic CRUD calls use. */
interface LooseClient {
  GET(path: string, init?: object): Promise<FetchResult>;
  POST(path: string, init?: object): Promise<FetchResult>;
  PUT(path: string, init?: object): Promise<FetchResult>;
  DELETE(path: string, init?: object): Promise<FetchResult>;
}

function unwrap<T>({ data, error, response }: FetchResult): T {
  if (!response.ok) {
    const problem = (error ?? {}) as ApiProblem;
    throw { ...problem, status: problem.status ?? response.status } satisfies ApiProblem;
  }
  return data as T;
}

const ifMatch = (record: CrudRecord) => ({ 'If-Match': `"${String(record.version)}"` });

/**
 * Calls for a resource mapped with the backend's MapCrud (list, create, update, delete) at `path`, e.g.
 * `/api/v1/identity/brands`. Pass a client from createHoApi or createStoreApi.
 */
export function crudApi<T extends CrudRecord, TInput>(client: object, path: string): CrudApi<T, TInput> {
  const api = client as LooseClient;
  return {
    path,
    list: async (params) => {
      const page = unwrap<{ items: T[]; total: number | string; totalCapped?: boolean }>(await api.GET(path, { params: { query: params } }));
      return { items: page.items, total: Number(page.total), totalCapped: page.totalCapped ?? false };
    },
    get: async (id) => unwrap<T>(await api.GET(`${path}/{id}`, { params: { path: { id } } })),
    create: async (input) => unwrap<T>(await api.POST(path, { body: input })),
    update: async (record, input) =>
      unwrap<T>(
        await api.PUT(`${path}/{id}`, { params: { path: { id: record.id } }, body: input, headers: ifMatch(record) }),
      ),
    remove: async (record) => {
      unwrap<unknown>(
        await api.DELETE(`${path}/{id}`, { params: { path: { id: record.id } }, headers: ifMatch(record) }),
      );
    },
    restore: async (record) =>
      unwrap<T>(await api.POST(`${path}/{id}/restore`, { params: { path: { id: record.id } }, headers: ifMatch(record) })),
  };
}
